import React, { useCallback, useEffect, useState } from 'react';
import { Card, Link, ListItemIcon, ListItemText, Typography, TextField } from '@mui/material';
import Select from '@mui/material/Select';
import { USER_TYPES, processAdminUsersReportRequest, updateUser } from '../../services/user';
import { addCompanyEmployee, getCompany } from '../../services/company';
import MasqueradeMenuItem from '../Masquerade/MasqueradeMenuItem';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import moment from 'moment';
import BooleanFilterComponent from '../Admin/BooleanFilter';
import {
  DataGridPro,
  GridColDef,
  GridOverlay,
  GridRowParams,
  GridSortModel,
  GridFilterModel,
} from '@mui/x-data-grid-pro';

import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PeopleIcon from '@mui/icons-material/People';

import User, { Company } from '../../interfaces/users.interface';
import { getUsersForDataGrid, resendUserInvite, resetUser, getUser, deleteUser } from '../../services/user';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import Button from '@mui/material/Button';
import NewUserForm from '../Users/NewUserForm';
import StandardDialog, { StandardDialogActions } from '../Modals/StandardDialog';
import Stack from '@mui/material/Stack';
import UserForm from '../Users/UserForm';
import { COMPANY_TYPES } from '../../services/vendor';
import BaseToolbar from '../Datagrid/BaseToolbar';
import GridPaginationBar from './GridPaginationBar';
import UserTypeFilter from './UserTypeFilter';
import CompanyTypeFilter from './CompanyTypeFilter';
import { stringContainsOnly } from '../../utils/adminDashboardUtils';
import ReportRequestButton from './ReportRequestButton';
import { useAppSelector } from '../../app/hooks';

interface UserRow {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  company: string | null;
  type: string;
  createdAt: string | null;
  lastLogin: string | null;
  actions: JSX.Element | string;
  isCognitoUserActive: boolean;
}

interface RowActionsMenuProps {
  anchorEl: HTMLElement | null;
  handleClose: any;
  currentRow: any;
  setRows: (newRows: UserRow[]) => void;
  rows: UserRow[];
  canMasquerade: undefined | boolean;
}

function createUserRow(user: any) {
  const isAdminUser = user.company?.admins?.includes(user.id);
  let userType: string = '';
  const { id, firstName, isConsultant, lastName, email, company, type, createdAt, lastLogin, isCognitoUserActive } = user;
  if (type === USER_TYPES.VENDOR_ADMIN) userType = 'Service Provider';
  if (type === USER_TYPES.ASSESSOR) userType = 'Assessor';
  if (type === USER_TYPES.CONTENT_OWNER) userType = 'Content Owner';
  if (type === USER_TYPES.TPN_ADMIN) userType = 'TPN Admin';

  if (type !== USER_TYPES.ASSESSOR) userType += isAdminUser && type !== USER_TYPES.TPN_ADMIN ? ' Admin' : ' User';

  if (type !== USER_TYPES.ASSESSOR && type !== USER_TYPES.CONTENT_OWNER) {
    userType += isConsultant ? ' Consultant' : '';
  }

  return {
    id,
    firstName,
    lastName,
    email,
    company: company?.name || '',
    companyId: company?.companyId || null,
    type: userType,
    createdAt,
    lastLogin,
    companyType: company?.type || '',
    actions: '',
    isCognitoUserActive,
    isConsultant,
  };
}

function CustomToolbar(props: { onInviteUserSuccess: (u: User) => void; [k: string]: any }) {
  const [openInviteAdminModal, setOpenInviteAdminModal] = useState(false);

  const handleInviteAdminSuccess = (newUser: User) => {
    setOpenInviteAdminModal(false);
    props.onInviteUserSuccess(newUser);
  };

  const handleProcessAdminUsersReportRequest = useCallback(async () => {
    try {
      await processAdminUsersReportRequest();
    } catch (error) {
      console.error('Failed to process admin users report request:', error);
      throw error;
    }
  }, []);

  return (
    <BaseToolbar {...props}
    exportExtraItems={
      <ReportRequestButton
        type="Users"
        onRequest={handleProcessAdminUsersReportRequest}
      />
    }>
      <div className="flex items-center gap-2">
      <StandardDialog
        title="Invite TPN Admin"
        handleClose={() => setOpenInviteAdminModal(false)}
        isOpen={openInviteAdminModal}
      >
        <NewUserForm
          successCallback={handleInviteAdminSuccess}
          modalClose={() => setOpenInviteAdminModal(false)}
          userType={USER_TYPES.TPN_ADMIN}
        />
      </StandardDialog>
      <Button
        variant="text"
        size="small"
        startIcon={<PersonAddAlt1OutlinedIcon />}
        onClick={() => setOpenInviteAdminModal(true)}
      >
        Invite TPN Admin
      </Button>
      </div>
    </BaseToolbar>
  );
}

function RowActionsMenu({ anchorEl, handleClose, currentRow, setRows, rows, canMasquerade }: RowActionsMenuProps) {
  const open = Boolean(anchorEl);
  const [rowUser, setRowUser] = useState<User>();
  const [isFetchingUser, setIsFetchingUser] = useState(false);
  const [deleteUserOpen, setDeleteUserOpen] = useState(false);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [associateOrphanedUserOpen, setAssociateOrphanedUserOpen] = useState(false);
  const [associateCompanyId, setAssociateCompanyId] = useState<string | number | null>(null);
  const [associateCompany, setAssociateCompany] = useState<Company | null>(null);

  const handleResendInvite = () => {
    handleClose();
    if (rowUser) resendUserInvite(rowUser);
  };

  const handleResetUser = () => {
    handleClose();
    if (rowUser) resetUser(rowUser);
  };

  const handleEditUserSubmit = () => {
    setEditUserOpen(false);
    handleClose();
  };

  const handleUserDeleteSubmit = () => {
    if (!rowUser) return;
    deleteUser(rowUser).then(() => {
      setRows(rows.filter((row) => row.id !== rowUser.id));
      setDeleteUserOpen(false);
      handleClose();
    });
  };

  const updateCompanyEmployeesAndUpdateUser = async (companyId: any) => {
    if (!companyId) return;

    try {
      const company = await addCompanyEmployee(companyId, currentRow.id);
      const user = await getUser(currentRow.id);

      const updatedUser = { ...user, company };
      const updatedUserRow = createUserRow(updatedUser);

      setRowUser(updatedUser);
      setRows(rows.map((row) => (row.id === user.id ? updatedUserRow : row)));
      setAssociateOrphanedUserOpen(false);
      handleClose();
    } catch (error) {
      console.error('Error updating company employees and user:', error);
    }
  };

  const handleCompanyIdLookup = async (companyId: any) => {
    if (!companyId) return;
    setAssociateCompanyId(companyId);
    await getCompany(companyId).then((company: any) => setAssociateCompany(company));
  };

  useEffect(() => {
    if (currentRow) {
      getUser(currentRow.id).then((user) => {
        setRowUser(user);
        setIsFetchingUser(false);
      });
    }
  }, [currentRow]);

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >{rowUser &&
      <div>
        <StandardDialog title={`Delete ${rowUser.email}?`} handleClose={() => setDeleteUserOpen(false)} isOpen={deleteUserOpen}>
            <Typography>Are you sure you want delete {rowUser.email}?</Typography>
            <StandardDialogActions>
                <Button variant="outlined" onClick={() => setDeleteUserOpen(false)}>Cancel</Button>
                <Button variant="outlined" color='error' startIcon={<DeleteIcon />} onClick={() => handleUserDeleteSubmit()}>Delete</Button>
            </StandardDialogActions>
        </StandardDialog>
        <StandardDialog title={`Edit ${rowUser.email}?`} handleClose={() => setEditUserOpen(false)} isOpen={editUserOpen}>
            <Stack direction="row" spacing={2}>
                <UserForm successCallback={handleEditUserSubmit} userType={rowUser.type} user={rowUser} />
            </Stack>
        </StandardDialog>
        <StandardDialog title='Associate Orphaned User' handleClose={() => setAssociateOrphanedUserOpen(false)} isOpen={associateOrphanedUserOpen}>
          <Typography>Associate User {rowUser.email} With {associateCompany ? associateCompany.name : 'A Company'}</Typography>
          <TextField
            sx={{ marginTop: 2 }}
            label="Company ID"
            onBlur={(e) => handleCompanyIdLookup(e.target.value)}
            variant="outlined"
          />
          <StandardDialogActions>
            <Button variant="outlined" onClick={() => setAssociateOrphanedUserOpen(false)}>Cancel</Button>
            <Button
              variant="outlined"
              color='error'
              onClick={() => updateCompanyEmployeesAndUpdateUser(associateCompanyId)}
            >Associate</Button>
          </StandardDialogActions>
        </StandardDialog>
      </div>
    }
      <MenuItem onClick={() => setEditUserOpen(true)}>
        <ListItemIcon><EditIcon /></ListItemIcon>
        <ListItemText>Edit</ListItemText>
      </MenuItem>
      <MenuItem onClick={() => setDeleteUserOpen(true)}>
        <ListItemIcon><DeleteIcon /></ListItemIcon>
        <ListItemText>Delete</ListItemText>
      </MenuItem>
      <MenuItem disabled={isFetchingUser} onClick={handleResendInvite}>
        <ListItemIcon><ManageAccountsOutlinedIcon /></ListItemIcon>
        <ListItemText>Resend Invite</ListItemText>
      </MenuItem>
      <MenuItem disabled={isFetchingUser} onClick={handleResetUser}>
        <ListItemIcon><ManageAccountsOutlinedIcon /></ListItemIcon>
        <ListItemText>Reset</ListItemText>
      </MenuItem>
      {rowUser && rowUser.companies.length === 0 && rowUser.type !== USER_TYPES.TPN_ADMIN && (
        <MenuItem onClick={() => setAssociateOrphanedUserOpen(true)}>
          <ListItemIcon><PeopleIcon /></ListItemIcon>
          <ListItemText>Associate</ListItemText>
        </MenuItem>
      )}
      {rowUser && rowUser.type !== USER_TYPES.TPN_ADMIN && canMasquerade && (
        <MasqueradeMenuItem userId={rowUser.id} userDisplay={rowUser.email} />
      )}
    </Menu>
  );
}

export default function UsersAdminPanel() {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);
  const [rows, setRows] = useState<UserRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentRow, setCurrentRow] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const { user } = useAppSelector(state => state.user);
  const canMasquerade = React.useMemo(() => user?.permissions?.includes('can_masquerade'), [user]);

  const inviteAdminSuccessHandler = (newUser: User) => {
    const newUserRow = createUserRow(newUser);
    setRows([...rows, newUserRow]);
  };

  const handleMenuClose = (event: any) => {
    if (event) event.preventDefault();
    setMenuAnchorEl(null);
    setCurrentRow(null);
  };

  function handleRowClick(params: GridRowParams, event: any) {
    setCurrentRow(params.row);
    setMenuAnchorEl(event.currentTarget);
  }

  const fetchDataGridUsers = useCallback(async (
    pageProp: number,
    pageSizeProp: number,
    sortModelProp: GridSortModel,
    filterModelProp: GridFilterModel,
  ) => {
    setIsLoading(true);

    try {
      const { results, count } = await getUsersForDataGrid({
        page: pageProp,
        pageSize: pageSizeProp,
        sortModel: sortModelProp,
        filterModel: filterModelProp,
      });

      const mappedRows = results.map(createUserRow);
      setRows(mappedRows);
      setRowCount(count);
    } catch (error) {
      console.error('Failed to fetch users for data grid:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);


  useEffect(() => {
    fetchDataGridUsers(page, pageSize, sortModel, filterModel);
  }, [fetchDataGridUsers, page, pageSize, sortModel, filterModel]);

  const handleUserIsActiveChange = useCallback(async (params: any, isCognitoUserActive: boolean) => {
    const { id, email, firstName, lastName, type, lastLogin, isConsultant } = params.row;
    const newUser = { id, email, firstName, lastName, type, lastLogin, isConsultant, isCognitoUserActive } as any;
    try {
      await updateUser(newUser);
      setRows((prevRows) => {
        const newRows = [...prevRows];
        const rowIndex = newRows.findIndex((row) => row.id === id);
        if (rowIndex > -1) {
          newRows[rowIndex] = { ...newRows[rowIndex], isCognitoUserActive };
        }
        return newRows;
      });
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  }, []);

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', flex: 1, filterOperators: stringContainsOnly  },
    { field: 'firstName', headerName: 'First Name',  flex: 2, filterOperators: stringContainsOnly },
    { field: 'lastName', headerName: 'Last Name',  flex: 2, filterOperators: stringContainsOnly },
    { field: 'email', headerName: 'Email', flex: 3,  filterOperators: stringContainsOnly, renderCell: (params) => <Link onClick={(e) => e.stopPropagation()} href={`mailto:${params.row.email}`}>{params.row.email}</Link> },
    { field: 'company', headerName: 'Company', flex: 3,  filterOperators: stringContainsOnly, renderCell: (params) => <Link onClick={(e) => e.stopPropagation()} href={`registry/${params.row.companyId}`}>{params.row.company}</Link> },
    { field: 'companyType', headerName: 'Company Type', flex: 2, valueGetter: (params) => {
      if (!params.value) return '';
      if (params.value === COMPANY_TYPES.VENDOR) return 'Service Provider';
      if (params.value === COMPANY_TYPES.ASSESSOR) return 'Assessor';
      if (params.value === COMPANY_TYPES.CONTENT_OWNER) return 'Content Owner';
      return params.value as string;
    },
    filterOperators: [{
      label: 'Is',
      value: 'is',
      getApplyFilterFn: () => null,
      InputComponent: CompanyTypeFilter,
    }],
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 3,
      filterOperators: [{
        label: 'Is',
        value: 'is',
        getApplyFilterFn: () => null,
        InputComponent: UserTypeFilter,
      }],
    },
    {
      field: 'isCognitoUserActive',
      headerName: 'Is Active',
      flex: 3,
      renderCell: (params) => (
        <Select
          label="Is Active"
          value={params.value ? 'Yes' : 'No'}
          onChange={(event) => {
            // Handle change to convert 'Yes'/'No' back to true/false and update accordingly
            const isActive = event.target.value === 'Yes';
            handleUserIsActiveChange(params as any, isActive);
          }}
          size="small"
          sx={{
            width: '100%',
            border: 'none', // Remove border
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none', // Ensure no border is visible
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: 'none', // Remove border on hover
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: 'none', // Remove border when the select is focused
              boxShadow: 'none', // Optionally remove shadow if any
            },
          }}
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </Select>),
      filterOperators: [
        {
          label: 'Is',
          value: 'is',
          getApplyFilterFn: (filterItem) => {
            return ({ value }) =>  value === filterItem.value;
          },
          InputComponent: BooleanFilterComponent,
        },
      ],
    },
    { field: 'createdAt', headerName: 'Created At', flex: 2, valueFormatter: (params) => {
      if (!params.value) return '';
      return moment(new Date(params.value as string).toLocaleDateString()).format('MM/DD/YYYY');
    } },
    { field: 'lastLogin', headerName: 'Last Login', flex: 2, valueFormatter: (params) => {
      if (!params.value) return '';
      return moment(new Date(params.value as string).toLocaleDateString()).format('MM/DD/YYYY');
    } },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: () => (
        <IconButton>
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Card sx={{ padding: '2em' }}>
      <DataGridPro
        rows={rows}
        rowCount={rowCount}
        columns={columns}
        loading={isLoading}
        components={{
          Toolbar: CustomToolbar,
          Footer: GridPaginationBar,
          LoadingOverlay: () => (
            <GridOverlay>
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                <CircularProgress />
              </div>
            </GridOverlay>
          ),
        }}
        componentsProps={{
          toolbar: {
            csvOptions: {
              fileName: `tpn_users_${new Date().toLocaleDateString()}`,
              fields: ['id', 'firstName', 'lastName', 'email', 'company', 'companyType', 'type', 'createdAt', 'isCognitoUserActive', 'lastLogin'],
            },
            onInviteUserSuccess: inviteAdminSuccessHandler,
          },
        }}
        onRowClick={handleRowClick}
        disableSelectionOnClick
        pagination
        paginationMode="server"
        page={page}
        pageSize={pageSize}
        rowsPerPageOptions={[20, 50]}
        onPageChange={(newPage) => {
          setPage(newPage);
        }}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setPage(0);
        }}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={(newModel) => {
          setSortModel(newModel);
          setPage(0);
        }}

        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={(newModel) => {
          setFilterModel(newModel);
          setPage(0);
        }}
      />
      <RowActionsMenu
        anchorEl={menuAnchorEl}
        handleClose={(e: any) => handleMenuClose(e)}
        currentRow={currentRow}
        setRows={setRows}
        rows={rows}
        canMasquerade={canMasquerade}
      />
    </Card>
  );
}
